<script setup>
import { computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

const props = defineProps({ data: { type: Object, required: true } });
const emit = defineEmits(['update', 'remove', 'close']);
const { t } = useI18n();
const store = useStore();
const teams = computed(() => store.getters['teams/getTeams'] || []);
const agents = computed(() => store.getters['agents/getAgents'] || []);
const target = computed(() => props.data.escalateTo || 'ai_team');
const isAiTeam = computed(() => target.value === 'ai_team');
const isTeam = computed(() => target.value === 'team');
const teamId = computed(() => props.data.teamId || '');
const allowedTeamIds = computed(() =>
  (props.data.allowedTeamIds || []).map(Number)
);
const allTeams = computed(
  () =>
    props.data.allTeams === true ||
    (props.data.allTeams == null && !allowedTeamIds.value.length)
);
const selectedTeams = computed(() => {
  if (isTeam.value)
    return teams.value.filter(team => Number(team.id) === Number(teamId.value));
  if (!isAiTeam.value) return [];
  return allTeams.value
    ? teams.value
    : teams.value.filter(team =>
        allowedTeamIds.value.includes(Number(team.id))
      );
});
const selectedTeamIds = computed(() =>
  selectedTeams.value.map(team => Number(team.id))
);
const teamsWithoutDescription = computed(() =>
  selectedTeams.value
    .filter(team => !(team.description || '').trim())
    .map(team => team.name)
);
const assignmentMode = computed(() => {
  if (props.data.assignmentMode) return props.data.assignmentMode;
  if (!isAiTeam.value) return 'team_queue';

  return props.data.onlineOnly === false
    ? 'round_robin_all'
    : 'round_robin_online';
});
const assignmentHint = computed(() => {
  const keys = {
    team_queue: 'AI_AGENTS.FLOW.ESCALATE.TEAM_QUEUE_HINT',
    round_robin_online: 'AI_AGENTS.FLOW.ESCALATE.ROUND_ROBIN_ONLINE_HINT',
    round_robin_all: 'AI_AGENTS.FLOW.ESCALATE.ROUND_ROBIN_ALL_HINT',
  };
  return t(keys[assignmentMode.value]);
});
const usesRotation = computed(() =>
  assignmentMode.value.startsWith('round_robin')
);
const participantMap = computed(() => props.data.participantIdsByTeam || {});
const isFetchingMembers = computed(
  () => store.getters['teamMembers/getUIFlags']?.isFetching
);
const teamMembers = team =>
  store.getters['teamMembers/getTeamMembers'](team.id) || [];
const participantIds = team => {
  const configured = participantMap.value[String(team.id)];
  return configured == null
    ? teamMembers(team).map(member => Number(member.id))
    : configured.map(Number);
};

onMounted(() => {
  store.dispatch('teams/get');
  store.dispatch('agents/get');
});
watch(
  selectedTeamIds,
  ids =>
    ids.forEach(id =>
      Promise.resolve(store.dispatch('teamMembers/get', { teamId: id })).catch(
        () => {}
      )
    ),
  { immediate: true }
);

const setTarget = event => {
  const value = event.target.value;
  emit('update', {
    escalateTo: value,
    teamId: null,
    assigneeId: null,
    defaultTeamId: null,
    allTeams: value === 'ai_team',
    allowedTeamIds: [],
    assignmentMode: value === 'ai_team' ? 'round_robin_online' : 'team_queue',
    participantIdsByTeam: {},
  });
};
const onTeam = event =>
  emit('update', {
    teamId: event.target.value || null,
    assigneeId: null,
    participantIdsByTeam: {},
  });
const onUser = event =>
  emit('update', { assigneeId: event.target.value || null });
const toggleAllTeams = event =>
  emit('update', {
    allTeams: event.target.checked,
    allowedTeamIds: [],
    defaultTeamId: null,
    participantIdsByTeam: {},
  });
const toggleAllowedTeam = (id, checked) => {
  const next = allowedTeamIds.value.filter(value => value !== Number(id));
  if (checked) next.push(Number(id));
  emit('update', { allTeams: false, allowedTeamIds: next });
};
const toggleMember = (team, id, checked) => {
  const next = participantIds(team).filter(value => value !== Number(id));
  if (checked) next.push(Number(id));
  emit('update', {
    participantIdsByTeam: { ...participantMap.value, [String(team.id)]: next },
  });
};
</script>

<template>
  <div class="p-4">
    <div class="mb-1 flex items-center justify-between">
      <div class="flex items-center gap-2">
        <span class="i-lucide-life-buoy text-n-slate-11" />
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ data.label || t('AI_AGENTS.FLOW.ESCALATE.TITLE') }}
        </h3>
      </div>
      <button class="i-lucide-x text-n-slate-10" @click="emit('close')" />
    </div>
    <p class="mb-4 text-xs text-n-slate-10">
      {{ t('AI_AGENTS.FLOW.ESCALATE.SUBTITLE') }}
    </p>

    <label class="mb-1 block text-xs text-n-slate-10">{{
      t('AI_AGENTS.FLOW.ESCALATE.TARGET')
    }}</label>
    <select
      :value="target"
      data-testid="escalate-target"
      class="mb-4 w-full rounded-lg border border-n-weak bg-n-solid-1 px-2.5 py-1.5 text-sm"
      @change="setTarget"
    >
      <option value="ai_team">
        {{ t('AI_AGENTS.FLOW.ESCALATE.TARGET_AI_TEAM') }}
      </option>
      <option value="team">
        {{ t('AI_AGENTS.FLOW.ESCALATE.TARGET_TEAM') }}
      </option>
      <option value="user">
        {{ t('AI_AGENTS.FLOW.ESCALATE.TARGET_USER') }}
      </option>
    </select>

    <template v-if="isTeam">
      <label class="mb-1 block text-xs text-n-slate-10">{{
        t('AI_AGENTS.FLOW.ESCALATE.TEAM')
      }}</label>
      <select
        :value="teamId"
        data-testid="escalate-team"
        class="mb-4 w-full rounded-lg border border-n-weak bg-n-solid-1 px-2.5 py-1.5 text-sm"
        @change="onTeam"
      >
        <option value="">{{ t('AI_AGENTS.FLOW.ESCALATE.TEAM_PH') }}</option>
        <option v-for="team in teams" :key="team.id" :value="team.id">
          {{ team.name }}
        </option>
      </select>
    </template>
    <template v-else-if="target === 'user'">
      <label class="mb-1 block text-xs text-n-slate-10">{{
        t('AI_AGENTS.FLOW.ESCALATE.USER')
      }}</label>
      <select
        :value="data.assigneeId || ''"
        data-testid="escalate-user"
        class="mb-3 w-full rounded-lg border border-n-weak bg-n-solid-1 px-2.5 py-1.5 text-sm"
        @change="onUser"
      >
        <option value="">{{ t('AI_AGENTS.FLOW.ESCALATE.USER_PH') }}</option>
        <option v-for="agent in agents" :key="agent.id" :value="agent.id">
          {{ agent.name }}
        </option>
      </select>
    </template>

    <template v-if="isAiTeam">
      <p class="mb-3 rounded-lg bg-n-slate-3 p-2 text-xs text-n-slate-11">
        {{ t('AI_AGENTS.FLOW.ESCALATE.AI_TEAM_HINT') }}
      </p>
      <label
        class="mb-2 flex items-center gap-2 text-sm font-medium text-n-slate-12"
      >
        <input
          type="checkbox"
          data-testid="escalate-all-teams"
          :checked="allTeams"
          @change="toggleAllTeams"
        />
        {{ t('AI_AGENTS.FLOW.ESCALATE.ALL_TEAMS') }}
      </label>
      <p class="mb-2 text-xs text-n-slate-10">
        {{ t('AI_AGENTS.FLOW.ESCALATE.AI_TEAM_ALLOWED') }}
      </p>
      <div class="mb-3 flex flex-col gap-2">
        <label
          v-for="team in teams"
          :key="team.id"
          class="flex items-start gap-2 text-sm text-n-slate-11"
          :class="{ 'opacity-50': allTeams }"
        >
          <input
            type="checkbox"
            class="mt-0.5"
            :disabled="allTeams"
            :checked="allTeams || allowedTeamIds.includes(Number(team.id))"
            @change="toggleAllowedTeam(team.id, $event.target.checked)"
          />
          <span
            ><span class="font-medium text-n-slate-12">{{ team.name }}</span
            ><span class="block text-xs text-n-slate-10">{{
              team.description ||
              t('AI_AGENTS.FLOW.ESCALATE.AI_TEAM_NO_DESCRIPTION')
            }}</span></span
          >
        </label>
      </div>
      <p
        v-if="teamsWithoutDescription.length"
        class="mb-3 rounded-lg bg-n-amber-3 p-2 text-xs text-n-amber-11"
      >
        {{
          t('AI_AGENTS.FLOW.ESCALATE.AI_TEAM_MISSING_DESCRIPTION', {
            teams: teamsWithoutDescription.join(', '),
          })
        }}
      </p>
    </template>

    <template v-if="isAiTeam || (isTeam && teamId)">
      <div class="my-4 border-t border-n-weak" />
      <label class="mb-1 block text-xs text-n-slate-10">{{
        t('AI_AGENTS.FLOW.ESCALATE.ASSIGNMENT')
      }}</label>
      <select
        :value="assignmentMode"
        data-testid="escalate-assignment"
        class="mb-2 w-full rounded-lg border border-n-weak bg-n-solid-1 px-2.5 py-1.5 text-sm"
        @change="emit('update', { assignmentMode: $event.target.value })"
      >
        <option value="team_queue">
          {{ t('AI_AGENTS.FLOW.ESCALATE.ASSIGNMENT_QUEUE') }}
        </option>
        <option value="round_robin_online">
          {{ t('AI_AGENTS.FLOW.ESCALATE.ASSIGNMENT_ONLINE') }}
        </option>
        <option value="round_robin_all">
          {{ t('AI_AGENTS.FLOW.ESCALATE.ASSIGNMENT_ALL') }}
        </option>
      </select>
      <p class="mb-3 text-xs text-n-slate-10">
        {{ assignmentHint }}
      </p>

      <details
        v-if="usesRotation"
        class="rounded-lg border border-n-weak bg-n-solid-2 p-3"
      >
        <summary class="cursor-pointer text-sm font-medium text-n-slate-12">
          {{ t('AI_AGENTS.FLOW.ESCALATE.CUSTOMIZE_MEMBERS') }}
        </summary>
        <p class="mt-2 text-xs text-n-slate-10">
          {{ t('AI_AGENTS.FLOW.ESCALATE.CUSTOMIZE_MEMBERS_HINT') }}
        </p>
        <p v-if="isFetchingMembers" class="mt-3 text-xs text-n-slate-10">
          {{ t('AI_AGENTS.FLOW.ESCALATE.MEMBERS_LOADING') }}
        </p>
        <div v-for="team in selectedTeams" :key="team.id" class="mt-3">
          <div class="mb-1 flex items-center justify-between">
            <strong class="text-xs text-n-slate-12">{{ team.name }}</strong
            ><span class="text-xs tabular-nums text-n-slate-10"
              >{{ participantIds(team).length }}/{{
                teamMembers(team).length
              }}</span
            >
          </div>
          <p v-if="!teamMembers(team).length" class="text-xs text-n-slate-10">
            {{ t('AI_AGENTS.FLOW.ESCALATE.MEMBERS_EMPTY') }}
          </p>
          <label
            v-for="member in teamMembers(team)"
            :key="`${team.id}-${member.id}`"
            class="flex items-center gap-2 py-1 text-sm text-n-slate-11"
          >
            <input
              type="checkbox"
              :data-testid="`escalate-member-${team.id}-${member.id}`"
              :checked="participantIds(team).includes(Number(member.id))"
              @change="toggleMember(team, member.id, $event.target.checked)"
            />
            {{ member.name }}
          </label>
          <p
            v-if="teamMembers(team).length && !participantIds(team).length"
            class="mt-1 text-xs text-n-amber-11"
          >
            {{ t('AI_AGENTS.FLOW.ESCALATE.MEMBERS_NONE') }}
          </p>
        </div>
      </details>
    </template>

    <button
      type="button"
      class="mt-8 w-full text-sm font-medium text-n-ruby-10"
      @click="emit('remove')"
    >
      {{ t('AI_AGENTS.FLOW.REMOVE_NODE') }}
    </button>
  </div>
</template>
